import React, {
  useState, useEffect, useRef, useCallback, Suspense,
} from 'react';
import { isMobile } from '$/utils';
import { getIsOverseaByCountryCode } from '$/common/utils/countryCodeUtil';
import { getHomeFeaturedList } from '$/domains/caseV3/caseV3Service';
import {
  getHomeCaseList,
} from '$/domains/material/materialService';
import type { CardItem } from '$/domains/material/type';
import type {
  V3CanvasHomeFeaturedItem,
  V3CanvasHomeFeaturedRegion,
} from '$/domains/caseV3/type';
import { useOfficialHomeContext } from '@/components/overseaHome/OfficialHomeContext';

const CaseListMobile = React.lazy(() => import('./CaseListMobile'));
const CaseListWeb = React.lazy(() => import('./CaseListWeb'));

/** 占位高度，避免懒加载前布局塌陷 */
const PLACEHOLDER_MIN_HEIGHT = 200;

/**
 * 将旧官网案例转换为当前展示组件所需结构。
 * 海外精选投放尚未配置时仅用于展示兜底，点击后仍沿用旧案例的 card_id 创作链路。
 */
function adaptLegacyCase(
  item: CardItem,
  index: number,
  region: V3CanvasHomeFeaturedRegion,
  locale: string,
): V3CanvasHomeFeaturedItem {
  const media = item.media ?? {
    width: 0,
    height: 0,
    url: '',
    type: 'image' as const,
    cover: '',
    duration: 0,
  };

  return {
    featured_id: item.card_id,
    featured_version: 0,
    presentation: {},
    case_id: item.card_id,
    work_id: item.card_id,
    category_id: 0,
    placement: 'website_home',
    region,
    locale,
    card_override: null,
    casting_ip_count: 0,
    casting_ips: [],
    ip_display_mode: 'legacy',
    copy_allowed: false,
    copy_disabled_reason: 'legacy_fallback',
    case: {
      case_id: item.card_id,
      case_type: String(item.card_type),
      title: item.title,
      desc: item.desc,
      cover: item.cover,
      prompt: item.prompt,
      user: {
        screen_name: item.user?.screen_name ?? '',
        avatar: item.user?.avatar ?? '',
        uid: 0,
      },
      source_type: 'legacy',
      source_id: item.link,
      cover_media: media,
      work_media: media,
      input_media: [],
      reference_media: [],
      category_id: 0,
      index,
      used_num: String(item.used_num ?? 0),
      icon: item.icon,
      created_at: item.created_at,
    },
  };
}

/** 官网精选案例区，进入预加载范围后按当前地区和语言拉取投放数据。 */
export default function CaseList() {
  const officialHome = useOfficialHomeContext();
  const [inView, setInView] = useState(false);
  const [list, setList] = useState<V3CanvasHomeFeaturedItem[]>([]);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const onIntersect = useCallback<IntersectionObserverCallback>((entries) => {
    const [entry] = entries;
    if (entry?.isIntersecting) setInView(true);
  }, []);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(onIntersect, {
      root: null,
      rootMargin: '600px',
      threshold: 0,
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [onIntersect]);

  useEffect(() => {
    if (!inView) return undefined;
    let cancelled = false;

    /** 官网投放位显式传入地区和 AdminDB 语言，避免依赖全局 Store 的初始化时序。 */
    const loadCaseList = async () => {
      const countryCode = officialHome?.countryCode ?? 'US';
      const isOversea = getIsOverseaByCountryCode(countryCode);
      const region: V3CanvasHomeFeaturedRegion = isOversea ? 'overseas' : 'cn';
      const locale = officialHome?.languageCode === 'zh-Hans' ? 'cn' : 'en';

      try {
        const result = await getHomeFeaturedList({
          placement: 'website_home',
          region,
          locale,
          limit: 50,
        });
        if (result.list?.length || !isOversea) {
          if (!cancelled) setList(result.list ?? []);
          return;
        }
      } catch (error) {
        if (!isOversea) {
          if (!cancelled) setList([]);
          return;
        }
      }

      try {
        // 海外精选投放未配置或请求失败时，继续展示旧官网已有案例，避免整块内容消失。
        const legacyResult = await getHomeCaseList({ category_id: 0, count: 50, page: 1 });
        const fallbackList = (legacyResult.list ?? []).map((item, index) => (
          adaptLegacyCase(item, index, region, locale)
        ));
        if (!cancelled) setList(fallbackList);
      } catch (error) {
        if (!cancelled) setList([]);
      }
    };

    loadCaseList();
    return () => {
      cancelled = true;
    };
  }, [inView, officialHome?.countryCode, officialHome?.languageCode]);

  const ListComponent = isMobile() ? CaseListMobile : CaseListWeb;

  return (
    <div ref={wrapperRef} style={!inView ? { minHeight: PLACEHOLDER_MIN_HEIGHT } : undefined}>
      {inView ? (
        <Suspense fallback={<div style={{ minHeight: PLACEHOLDER_MIN_HEIGHT }} />}>
          <ListComponent list={list} />
        </Suspense>
      ) : null}
    </div>
  );
}
